/**
 * NotificationsTray — visitor notifications surface (plan 112).
 * Mobile: full-screen fixed overlay. Desktop ≥md: right-anchored side panel.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faCheckDouble } from "@fortawesome/pro-regular-svg-icons/faCheckDouble";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons/faCircleInfo";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons/faCircleExclamation";
import { cn } from "@wondermaps/ui";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { getDismissKey } from "../../hooks/visitor/useNotificationsUnread";
import { analytics } from "../../lib/analytics";
import { ExpandableText } from "./ExpandableText";
import type { VisitorNotification } from "./notification-types";

export interface NotificationsTrayProps {
  open: boolean;
  notifications: VisitorNotification[];
  mapSlug: string;
  onClose: () => void;
}

function readIsDismissed(n: VisitorNotification, mapSlug: string): boolean {
  try {
    return localStorage.getItem(getDismissKey(n, mapSlug)) === "1";
  } catch {
    return false;
  }
}

function setDismissed(n: VisitorNotification, mapSlug: string) {
  try {
    localStorage.setItem(getDismissKey(n, mapSlug), "1");
    // Notify same-tab listeners (storage events only fire cross-tab)
    window.dispatchEvent(new StorageEvent("storage", { key: getDismissKey(n, mapSlug) }));
  } catch {
    // localStorage unavailable
  }
}

export function NotificationsTray({
  open,
  notifications,
  mapSlug,
  onClose,
}: NotificationsTrayProps) {
  const { t } = useTranslation("map");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [readVersion, setReadVersion] = useState(0);
  const [liveMessage, setLiveMessage] = useState("");

  // Esc closes; focus close button on open
  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const unread = notifications.reduce(
      (acc, n) => acc + (readIsDismissed(n, mapSlug) ? 0 : 1),
      0,
    );
    analytics.notificationsTrayOpened({
      unread_count: unread,
      total_count: notifications.length,
    });
  }, [open, notifications, mapSlug]);

  // Return focus to the bell on close
  useEffect(() => {
    if (open) return;
    const bell = document.querySelector<HTMLElement>('[aria-controls="notifications-tray"]');
    bell?.focus();
  }, [open]);

  const markRead = useCallback(
    (n: VisitorNotification) => {
      if (readIsDismissed(n, mapSlug)) return;
      setDismissed(n, mapSlug);
      setReadVersion((v) => v + 1);
    },
    [mapSlug],
  );

  const markAllRead = useCallback(() => {
    let unreadBefore = 0;
    for (const n of notifications) {
      if (!readIsDismissed(n, mapSlug)) {
        setDismissed(n, mapSlug);
        unreadBefore++;
      }
    }
    if (unreadBefore > 0) {
      setReadVersion((v) => v + 1);
      setLiveMessage(t("notifications.markedAllRead"));
      analytics.notificationsMarkAllRead({ unread_count_before: unreadBefore });
    }
  }, [notifications, mapSlug, t]);

  if (!open) return null;

  const unreadCount = notifications.reduce(
    (acc, n) => acc + (readIsDismissed(n, mapSlug) ? 0 : 1),
    0,
  );

  return (
    <div
      id="notifications-tray"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notifications-tray-title"
      className={cn(
        "fixed inset-0 z-40 bg-background flex flex-col",
        "md:inset-y-0 md:right-0 md:left-auto md:w-[420px] md:border-l md:shadow-xl",
      )}
    >
      <div className="flex items-center gap-2 border-b px-4 py-3 shrink-0 pt-safe">
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0 -ml-2 rounded-full hover:bg-muted transition-colors"
          aria-label={t("notifications.closeButton")}
        >
          <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
        </button>
        <h2 id="notifications-tray-title" className="font-semibold text-base flex-1 truncate">
          {t("notifications.title")}
        </h2>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border hover:bg-muted transition-colors"
          >
            <FontAwesomeIcon icon={faCheckDouble} className="h-3 w-3" aria-hidden />
            {t("notifications.markAllRead")}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto pb-safe">
        {notifications.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            {t("notifications.empty")}
          </div>
        ) : (
          <ul className="divide-y">
            {notifications.map((n) => (
              <li key={n.id}>
                <NotificationRow
                  notification={n}
                  mapSlug={mapSlug}
                  readVersion={readVersion}
                  onMarkRead={markRead}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sr-only" role="status" aria-live="polite">
        {liveMessage}
      </div>
    </div>
  );
}

interface NotificationRowProps {
  notification: VisitorNotification;
  mapSlug: string;
  readVersion: number;
  onMarkRead: (n: VisitorNotification) => void;
}

function NotificationRow({ notification: n, mapSlug, readVersion, onMarkRead }: NotificationRowProps) {
  const { t, i18n } = useTranslation("map");

  // readVersion forces re-eval when the tray bumps it
  const isUnread = !readIsDismissed(n, mapSlug);
  void readVersion;

  const stripe =
    n.type === "urgent"
      ? "bg-red-500"
      : n.type === "warning"
        ? "bg-amber-500"
        : "bg-blue-500";

  const icon =
    n.type === "urgent"
      ? faCircleExclamation
      : n.type === "warning"
        ? faTriangleExclamation
        : faCircleInfo;

  const iconColor =
    n.type === "urgent"
      ? "text-red-600 dark:text-red-400"
      : n.type === "warning"
        ? "text-amber-600 dark:text-amber-400"
        : "text-blue-600 dark:text-blue-400";

  const typeLabel =
    n.type === "urgent"
      ? t("notifications.types.urgent")
      : n.type === "warning"
        ? t("notifications.types.warning")
        : t("notifications.types.info");

  const time = formatRelativeTime(new Date(n.updatedAt).getTime(), t);
  const lang = i18n.language;

  const handleMarkRead = () => {
    analytics.notificationRead({
      notification_id: n.id,
      kind: n.kind,
      type: n.type,
      was_unread: isUnread,
    });
    if (isUnread) onMarkRead(n);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleMarkRead();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleMarkRead}
      onKeyDown={handleKeyDown}
      lang={lang}
      className="w-full text-left flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors items-start cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <div className={cn("w-1 self-stretch rounded-full shrink-0", stripe)} aria-hidden />
      <div
        className={cn(
          "w-2 h-2 rounded-full shrink-0 mt-2 transition-opacity",
          isUnread ? "bg-red-500 opacity-100" : "bg-transparent opacity-0",
        )}
        aria-label={isUnread ? t("notifications.unreadCount", { count: 1 }) : undefined}
      />
      <FontAwesomeIcon icon={icon} className={cn("h-4 w-4 mt-0.5 shrink-0", iconColor)} aria-label={typeLabel} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold truncate">{n.title}</span>
          <span className="text-[11px] text-muted-foreground shrink-0 ml-auto">{time}</span>
        </div>
        {n.body && (
          <ExpandableText
            text={n.body}
            lines={2}
            className="mt-1"
            textClassName="text-xs text-muted-foreground whitespace-pre-wrap break-words"
            onExpand={() =>
              analytics.notificationRowExpanded({
                notification_id: n.id,
                kind: n.kind,
                type: n.type,
              })
            }
          />
        )}
      </div>
    </div>
  );
}
